import type { RefObject } from 'react';
import { createContext } from 'react';

type SidePanelToggleContextValue = {
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  // Optional per-scope identity (e.g. current project/entity/context-type) provided by hosts
  // that scope their own open/closed session-storage override to more than a single global
  // bucket — currently only Sentiment's WidgetContainer. Left undefined for hosts that don't
  // set it (e.g. GenAI, which never mounts this provider at all), so consumers must treat it
  // as optional and fall back to their own scoping (see SidePanel.Container.tsx).
  scopeKey?: string;
};

export const SidePanelToggleContext = createContext<SidePanelToggleContextValue>({
  isOpen: false,
  openPanel: () => undefined,
  closePanel: () => undefined,
  scopeKey: undefined,
});

export const SidePanelHeightAnchorRefContext = createContext<RefObject<HTMLElement> | undefined>(undefined);

// True only when the panel is rendered inside `SidePanelArea` (see Layout.tsx), which
// animates its width open and shut. The panel reads this to know it must stay mounted for
// the length of the collapse; hosts that render `ChatSidePanel` directly have no such
// animation, so there it still unmounts the moment it is closed.
export const SidePanelAnimatedContext = createContext<boolean>(false);

// `MainArea` (see Layout.tsx) measures how far its own right edge sits from the viewport's
// right edge and provides the result here — 0 when nothing is eating into that space, and
// growing only when the side panel actually opens and shrinks it. Consumers add this to
// whatever base gutter their own stylesheet already uses (e.g. `right: calc(10px + var(--x))`)
// rather than the offset itself owning or duplicating that default.
export const MainAreaRightShiftContext = createContext<number>(0);
